import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { useEffect, useState } from "preact/hooks";
import { Button } from "../ui/button";
import { CircleCheck, Loader2, X } from "lucide-react";
import { OUTLINE_BRAND_BTN_CLASS, PRIMARY_BRAND_BTN_CLASS } from "./constants";

const MAX_LENGTH = 2000;

export interface ContactSupportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSend: (message: string) => void;
  sendPending: boolean;
  /** When true, render the plain "message sent" success screen (no rating prompt). */
  sendSuccess: boolean;
}

export function ContactSupportDialog({
  open,
  onOpenChange,
  onSend,
  sendPending,
  sendSuccess,
}: ContactSupportDialogProps) {
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (open) setMessage("");
  }, [open]);

  // Message sent — show a plain success (no rating prompt), like the cancel /
  // reactivate flows. Contact-support never routes through ActionResultDialog.
  if (sendSuccess) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          showCloseButton={false}
          onOpenChange={onOpenChange}
          overlayClassName="tw:z-70"
          className="tw:z-71 tw:p-4 tw:max-h-[85dvh] tw:flex tw:flex-col"
        >
          <div className="tw:flex tw:flex-col tw:items-center tw:justify-center tw:py-8 tw:px-4 tw:text-center tw:animate-in tw:fade-in tw:zoom-in-95 tw:duration-300">
            <div className="tw:rounded-full tw:bg-[#FDEDEE] tw:p-3 tw:mb-4">
              <CircleCheck
                className="tw:size-10 tw:text-[#523D33]"
                strokeWidth={1.5}
              />
            </div>
            <p
              className="tw:text-lg tw:font-semibold tw:m-0 tw:mb-2"
              style={{ color: "var(--so-text, #523D33)" }}
            >
              We&apos;ll get back to you shortly
            </p>
          </div>
          <DialogFooter className="tw:flex-col tw:sm:flex-col tw:gap-2 tw:mt-2 tw:pt-2">
            <Button
              type="button"
              onClick={() => onOpenChange(false)}
              className={PRIMARY_BRAND_BTN_CLASS}
            >
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  const trimmed = message.trim();
  const canSend = trimmed.length >= 2 && !sendPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        onOpenChange={onOpenChange}
        overlayClassName="tw:z-70"
        className="tw:z-71 tw:p-4"
      >
        <DialogHeader>
          <div className="tw:flex tw:flex-row tw:items-center tw:justify-between tw:gap-2 tw:w-full">
            <DialogTitle
              className="tw:min-w-0 tw:flex-1 tw:pr-1 tw:text-left"
              style={{ color: "var(--so-text, #523D33)" }}
            >
              Other
            </DialogTitle>
            <DialogClose
              data-slot="dialog-close"
              render={
                <Button
                  variant="ghost"
                  className="tw:p-0 tw:m-0 tw:shrink-0"
                  size="icon-sm"
                  aria-label="Close"
                />
              }
            >
              <X />
            </DialogClose>
          </div>
          <p className="tw:m-0 tw:text-xs tw:leading-relaxed tw:text-[#523D33]/80">
            What would you like to do?
          </p>
        </DialogHeader>

        <div className="tw:mt-2">
          <textarea
            value={message}
            onInput={(e) =>
              setMessage(
                (e.target as HTMLTextAreaElement).value.slice(0, MAX_LENGTH),
              )
            }
            disabled={sendPending}
            placeholder="Write here"
            rows={5}
            className="tw:w-full tw:rounded-lg tw:border tw:border-gray-200 tw:bg-white tw:px-3 tw:py-2.5 tw:text-base tw:resize-none tw:outline-none focus:tw:border-[#523D33] tw:transition-colors disabled:tw:opacity-60"
            style={{ color: "var(--so-text, #523D33)" }}
          />
          <div className="tw:mt-1 tw:text-right tw:text-xs tw:text-gray-400">
            {message.length}/{MAX_LENGTH}
          </div>
        </div>

        <DialogFooter className="tw:flex-col tw:sm:flex-col tw:gap-2 tw:mt-2 tw:pt-2">
          <Button
            type="button"
            disabled={!canSend}
            onClick={() => {
              if (trimmed.length >= 2) onSend(trimmed);
            }}
            className={PRIMARY_BRAND_BTN_CLASS}
          >
            {sendPending ? (
              <Loader2 className="tw:size-4 tw:animate-spin tw:mx-auto" />
            ) : (
              "Send"
            )}
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={sendPending}
            onClick={() => onOpenChange(false)}
            className={`${OUTLINE_BRAND_BTN_CLASS} tw:disabled:opacity-50`}
          >
            Never mind
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
